import React from "react";
import { useApp } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldAlert, Search, Download, Bell, LogOut, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { exportPosts } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNavigate } from "react-router-dom";

const TABS = [
  { k: "overview", label: "Overview" },
  { k: "map", label: "Geospatial Map" },
  { k: "analysis", label: "Analysis" },
  { k: "feed", label: "Live Feed" },
];

export default function NavigationRow() {
  const { activeTab, setActiveTab, filters, patchFilters, alerts, source, setSelectedPost } = useApp();
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="flex items-center justify-between border-b border-white/10 px-3 py-1" data-testid="navigation-row">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 pr-3 border-r border-white/10">
          <img src="/logo.png" alt="CIMAP" className="w-6 h-6 object-contain" width={24} height={24} />
          <div className="font-display text-xs tracking-[0.25em] uppercase">CIMAP</div>
          <span
            data-testid="data-source-indicator"
            className="label-micro px-1.5 py-0.5 border border-[var(--sev-low)] text-[var(--sev-low)]"
          >
            LIVE
          </span>
        </div>
        <nav className="flex" data-testid="nav-tabs">
          {TABS.map((t) => (
            <button
              key={t.k}
              data-testid={`nav-tab-${t.k}`}
              onClick={() => React.startTransition(() => setActiveTab(t.k))}
              className={`px-4 py-1.5 text-xs uppercase tracking-[0.15em] border-r border-white/10 first:border-l transition-colors ${
                activeTab === t.k
                  ? "bg-accent-strong"
                  : "bg-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-inset)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>
      <div className="flex items-center gap-2">

        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="notifications-btn" className="relative flex items-center gap-1.5 px-2.5 h-8 text-xs border border-white/15 hover:border-white/30 hover:bg-white/5">
              <Bell className="w-3.5 h-3.5" />
              {alerts.length > 0 && (
                <span
                className="absolute -top-1 -right-1 w-4 h-4 text-[9px] font-semibold bg-[var(--sev-critical)] text-black flex items-center justify-center"
                style={{ color: "#fff" }}
              >
                  {alerts.length > 9 ? "9+" : alerts.length}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-80 p-0 rounded-none bg-[var(--bg-surface)] border border-white/15 shadow-2xl z-[100]"
          >
            <div className="p-3 border-b border-white/10 bg-[var(--bg-panel)]">
              <div className="label-micro text-[var(--text-secondary)]">Critical Intelligence Alerts</div>
              <div className="font-display text-sm mt-0.5">{alerts.length} signals require attention</div>
            </div>
            <div className="max-h-80 overflow-y-auto bg-[var(--bg-surface)]">
              {alerts.length === 0 && (
                <div className="p-4 text-xs text-[var(--text-muted)] italic text-center">No active threats detected.</div>
              )}
              {alerts.map((a) => (
                <div
                  key={a.id}
                  onClick={() => setSelectedPost(a)}
                  className="p-3 border-b border-white/5 hover:bg-[var(--bg-inset)] transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-2 text-[10px] uppercase font-bold text-[var(--text-primary)]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--sev-critical)] pulse-dot" />
                    {a.venture || "Signal"} · {a.state || "—"}
                  </div>
                  <div className="text-xs mt-1 text-[var(--text-secondary)] leading-relaxed line-clamp-2">{a.content}</div>
                  <div className="label-micro mt-1 text-[var(--text-muted)]">{new Date(a.at).toLocaleTimeString()}</div>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>


        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-btn" className="flex items-center gap-2 px-2 h-8 border border-white/15 hover:border-white/30">
              <div className="w-5 h-5 bg-white/10 flex items-center justify-center text-[10px] font-semibold">
                {(user?.name || "U").slice(0, 1)}
              </div>
              <span className="text-xs">{user?.name || "User"}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-none bg-[var(--bg-surface)] border-white/15 text-white">
            <DropdownMenuLabel className="text-xs">
              <div className="font-display">{user?.name}</div>
              <div className="text-white/50 text-[10px]">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem
              data-testid="logout-btn"
              onClick={() => { logout(); nav("/login"); }}
              className="text-xs rounded-none"
            >
              <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
